import { PlusIcon } from '@primer/octicons-react';
import { ActionList, ActionMenu } from '@primer/react';
import { OfferingType, Tangibility } from '@upright/common/model/input/companies/products/ipr';
import { ATTRIBUTE_GUIDANCE } from '@upright/common/model/input/companies/products/ipr/attribute-guidance';
import { useState } from 'react';
import styled from 'styled-components';
import { ItemsContainer, ItemsList, SectionContainer } from './shared/section-styles';
import { SectionTitleWithGuidance } from './shared/section-guidance';
import { AddButton, ListToken } from './shared/tokens';

export type ProductTypeAttributeType = 'isManufactured' | 'Tangibility' | 'OfferingType';

interface ProductTypeProps {
  attributes: ProductTypeAttributeType[];
  isManufactured?: boolean;
  tangibility?: Tangibility;
  offeringType?: OfferingType;
  onIsManufacturedChange: (value: boolean | undefined) => void;
  onTangibilityChange: (value: Tangibility | undefined) => void;
  onOfferingTypeChange: (value: OfferingType | undefined) => void;
}

export interface TangibilityItem {
  id: Tangibility;
  title: string;
}

export interface OfferingTypeItem {
  id: OfferingType;
  title: string;
}

const TANGIBILITY_OPTIONS: TangibilityItem[] = Object.values(Tangibility).map(value => ({
  id: value,
  title: value,
}));

const OFFERING_TYPE_OPTIONS: OfferingTypeItem[] = Object.values(OfferingType).map(value => ({
  id: value,
  title: value,
}));

export function ProductTypeAttributes({
  attributes,
  isManufactured,
  tangibility,
  offeringType,
  onIsManufacturedChange,
  onTangibilityChange,
  onOfferingTypeChange,
}: ProductTypeProps) {
  const [isManufacturedMenuOpen, setIsManufacturedMenuOpen] = useState(false);
  const [isTangibilityMenuOpen, setIsTangibilityMenuOpen] = useState(false);
  const [isOfferingTypeMenuOpen, setIsOfferingTypeMenuOpen] = useState(false);

  const hasIsManufactured = attributes.includes('isManufactured');
  const hasTangibility = attributes.includes('Tangibility');
  const hasOfferingType = attributes.includes('OfferingType');

  const removeAttribute = (attributeId: ProductTypeAttributeType) => {
    if (attributeId === 'isManufactured') {
      onIsManufacturedChange(undefined);
    } else if (attributeId === 'Tangibility') {
      onTangibilityChange(undefined);
    } else {
      onOfferingTypeChange(undefined);
    }
  };

  return (
    <SectionContainer>
      <SectionTitleWithGuidance title="Product type attributes" guidance={ATTRIBUTE_GUIDANCE.productType} />

      <ItemsContainer>
        <ItemsList>
          {hasIsManufactured && (
            <ListToken
              size="medium"
              onRemove={() => {
                removeAttribute('isManufactured');
              }}
              text={isManufactured ? 'Manufactured by the company' : 'Not manufactured by the company'}
            />
          )}
          {hasTangibility && tangibility && (
            <ListToken
              size="medium"
              onRemove={() => {
                removeAttribute('Tangibility');
              }}
              text={`Tangibility: ${tangibility}`}
            />
          )}
          {hasOfferingType && offeringType && (
            <ListToken
              size="medium"
              onRemove={() => {
                removeAttribute('OfferingType');
              }}
              text={`Offering type: ${offeringType}`}
            />
          )}
          {!hasIsManufactured && (
            <ActionMenu open={isManufacturedMenuOpen} onOpenChange={setIsManufacturedMenuOpen}>
              <ActionMenu.Anchor>
                <AddButton size="medium" leadingVisual={PlusIcon} text="Add manufacturing status" />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay width="medium">
                <ScrollableActionListContainer>
                  <ActionList>
                    <ActionList.Group title="Select a product attribute...">
                      <ActionList.Item
                        onSelect={() => {
                          onIsManufacturedChange(true);
                          setIsManufacturedMenuOpen(false);
                        }}
                      >
                        Is manufactured
                      </ActionList.Item>
                      <ActionList.Item
                        onSelect={() => {
                          onIsManufacturedChange(false);
                          setIsManufacturedMenuOpen(false);
                        }}
                      >
                        Is not manufactured
                      </ActionList.Item>
                    </ActionList.Group>
                  </ActionList>
                </ScrollableActionListContainer>
              </ActionMenu.Overlay>
            </ActionMenu>
          )}
          {!hasTangibility && (
            <ActionMenu open={isTangibilityMenuOpen} onOpenChange={setIsTangibilityMenuOpen}>
              <ActionMenu.Anchor>
                <AddButton size="medium" leadingVisual={PlusIcon} text="Add tangibility" />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay width="medium">
                <ScrollableActionListContainer>
                  <ActionList>
                    <ActionList.Group title="Select tangibility...">
                      {TANGIBILITY_OPTIONS.map(option => (
                        <ActionList.Item
                          key={option.id}
                          onSelect={() => {
                            onTangibilityChange(option.id);
                            setIsTangibilityMenuOpen(false);
                          }}
                        >
                          {option.title}
                        </ActionList.Item>
                      ))}
                    </ActionList.Group>
                  </ActionList>
                </ScrollableActionListContainer>
              </ActionMenu.Overlay>
            </ActionMenu>
          )}
          {!hasOfferingType && (
            <ActionMenu open={isOfferingTypeMenuOpen} onOpenChange={setIsOfferingTypeMenuOpen}>
              <ActionMenu.Anchor>
                <AddButton size="medium" leadingVisual={PlusIcon} text="Add offering type" />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay width="medium">
                <ScrollableActionListContainer>
                  <ActionList>
                    <ActionList.Group title="Select offering type...">
                      {OFFERING_TYPE_OPTIONS.map(option => (
                        <ActionList.Item
                          key={option.id}
                          onSelect={() => {
                            onOfferingTypeChange(option.id);
                            setIsOfferingTypeMenuOpen(false);
                          }}
                        >
                          {option.title}
                        </ActionList.Item>
                      ))}
                    </ActionList.Group>
                  </ActionList>
                </ScrollableActionListContainer>
              </ActionMenu.Overlay>
            </ActionMenu>
          )}
        </ItemsList>
      </ItemsContainer>
    </SectionContainer>
  );
}

const ScrollableActionListContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;
